import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, CircularProgress, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useSnackbar } from 'notistack'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import CopyIcon from '../../common/CopyIcon'
import { statusBadgeSx } from '../../common/sharedStyles'
import { formatDate1 } from '../../common/util'
import { ApplyButton, FieldLabel, FilterActions, FilterDateField, FilterField, FilterRow, ResetButton } from '../../common/FilterBar'
import DateRangePicker from '../../components/DateRangePicker'
import ConfirmDialog from '../internal-alerts/ConfirmDialog'
import S3LinksDialog from './S3LinksDialog'
import {
    ACTIVE_STATUSES, RUN_STATUSES, archivalApi, cancelledBy, errorText, fmtBytes, fmtDateTime, fmtDuration, fmtNum, isCancelled,
    shortUser, statusLabel, statusTone, tierLabel,
} from './archivalUtils'

const API_FMT = 'yyyy-MM-dd'
const AUTO_REFRESH_MS = 30 * 1000
const EMPTY_FILTERS = { collection: '', status: '', from: null, to: null }

const S3_COLUMNS = ['s3']
const DASH = <Typography component='span' sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>—</Typography>
const oneLineSx = { display: 'block', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }
const numCol = { sortable: false, align: 'right', headerAlign: 'right' }

/** Single line with ellipsis; full text in the tooltip. */
function Ellipsis({ text, sx }) {
    if (!text) return DASH
    return (
        <Tooltip title={<Box component='span' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Box>} arrow placement='top-start'>
            <Typography component='span' sx={{ ...oneLineSx, ...sx }}>{text}</Typography>
        </Tooltip>
    )
}

function StatusCell({ row }) {
    const cancelled = isCancelled(row)
    const label = cancelled ? 'Cancelled' : statusLabel(row.status)
    const error = !cancelled && row.error ? row.error : ''
    const tip = cancelled
        ? `Cancelled by ${cancelledBy(row) || 'a user'}`
        : error || (row.dryRun && row.status !== 'DRY_RUN' ? 'Dry run — nothing was moved' : '')
    const badge = (
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box component='span' sx={{ ...statusBadgeSx(cancelled ? 'neutral' : statusTone(row.status)), whiteSpace: 'nowrap' }}>{label}</Box>
            {error && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} aria-label='Has error' />}
        </Box>
    )
    return tip ? <Tooltip title={tip} arrow>{badge}</Tooltip> : badge
}

/**
 * Run history (archive_audit_details RUN rows), server-side paginated. Re-reads every 30 s while any row on the
 * page is QUEUED or RUNNING; `reloadKey` lets the page force a re-read after it queues work, and `onSettled` tells
 * the page when the last active row on screen finished (so the cards' queued/running counts can be refreshed).
 */
export default function RunHistory({ collectionNames, globalS3Enabled, reloadKey, onSettled }) {
    const { enqueueSnackbar } = useSnackbar()
    const [pending, setPending] = useState(EMPTY_FILTERS)
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [page, setPage] = useState(0)
    const [sizePerPage, setSizePerPage] = useState(20)
    const [rows, setRows] = useState([])
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [linksRun, setLinksRun] = useState(null)
    const [cancelRun, setCancelRun] = useState(null)
    const [cancelling, setCancelling] = useState(false)
    const seq = useRef(0)
    const mounted = useRef(true)
    useEffect(() => () => { mounted.current = false }, [])

    const params = useMemo(() => {
        const p = { page, size: sizePerPage }
        if (filters.collection) p.collection = filters.collection
        if (filters.status) p.status = filters.status
        if (filters.from) p.from = formatDate1(filters.from, API_FMT)
        if (filters.to) p.to = formatDate1(filters.to, API_FMT)
        return p
    }, [filters, page, sizePerPage])

    const load = useCallback((silent = false) => {
        const mine = ++seq.current
        if (!silent) setLoading(true)
        return archivalApi.getRuns(params)
            .then((res) => {
                if (!mounted.current || mine !== seq.current) return
                setRows(res.data?.data || [])
                setCount(res.data?.count || 0)
            })
            .catch((err) => {
                if (!mounted.current || mine !== seq.current) return
                if (!silent) enqueueSnackbar(errorText(err, 'Failed to load run history'), { variant: 'error' })
            })
            // Whichever request is latest clears the spinner, so a silent re-read that supersedes a visible one cannot leave it stuck.
            .finally(() => { if (mounted.current && mine === seq.current) setLoading(false) })
    }, [params, enqueueSnackbar])

    useEffect(() => { load() }, [load, reloadKey])

    // Auto-refresh while anything on screen is still queued / running; the interval is cleared on unmount.
    const hasActive = rows.some((r) => ACTIVE_STATUSES.includes(r.status))
    useEffect(() => {
        if (!hasActive) return undefined
        const t = setInterval(() => load(true), AUTO_REFRESH_MS)
        return () => clearInterval(t)
    }, [hasActive, load])

    const wasActive = useRef(false)
    useEffect(() => {
        if (wasActive.current && !hasActive && onSettled) onSettled()
        wasActive.current = hasActive
    }, [hasActive, onSettled])

    const applyFilters = () => { setPage(0); setFilters(pending) }
    const resetFilters = () => { setPending(EMPTY_FILTERS); setPage(0); setFilters(EMPTY_FILTERS) }
    const pickRange = (r) => {
        if (!r || !r.length || !r[0].startDate || !r[0].endDate) return
        setPending((p) => ({ ...p, from: r[0].startDate, to: r[0].endDate }))
    }
    const pickerRange = useMemo(
        () => (pending.from && pending.to ? [{ startDate: pending.from, endDate: pending.to, key: 'selection' }] : []),
        [pending.from, pending.to]
    )

    const confirmCancel = () => {
        if (!cancelRun) return
        setCancelling(true)
        archivalApi.cancelRun(cancelRun.id)
            .then(() => {
                enqueueSnackbar(`Cancelled ${cancelRun.collectionName} ${cancelRun.period}`, { variant: 'success' })
                setCancelRun(null)
                load(true)
                if (onSettled) onSettled()
            })
            .catch((err) => enqueueSnackbar(errorText(err, 'Failed to cancel run'), { variant: 'error' }))
            .finally(() => setCancelling(false))
    }

    // S3 columns only matter once the S3 tier is on or some run on screen actually wrote parts.
    const showS3 = Boolean(globalS3Enabled) || rows.some((r) => r.s3Parts > 0)
    const columnVisibilityModel = useMemo(
        () => (showS3 ? {} : S3_COLUMNS.reduce((m, k) => ({ ...m, [k]: false }), {})),
        [showS3]
    )

    // Sized to fit the page width (no sideways scroll): long text lives in tooltips, not in extra columns.
    const columns = useMemo(() => [
        { field: 'status', headerName: 'Status', width: 101, sortable: false, renderCell: ({ row }) => <StatusCell row={row} /> },
        {
            field: 'collectionName', headerName: 'Collection', minWidth: 204, flex: 1, sortable: false,
            renderCell: ({ row }) => <Ellipsis text={row.collectionName} sx={{ fontFamily: 'monospace' }} />,
        },
        { field: 'period', headerName: 'Period', width: 76, sortable: false },
        {
            field: 'tiers', headerName: 'Move', width: 120, sortable: false,
            valueGetter: ({ row }) => `${tierLabel(row.fromTier)} → ${tierLabel(row.toTier)}`,
        },
        {
            field: 'docsMoved', headerName: 'Docs', width: 95, ...numCol,
            renderCell: ({ row }) => {
                if (row.docsMoved == null) return DASH
                const kept = row.skippedTouched > 0 ? row.skippedTouched : 0
                const cell = (
                    <Typography component='span' sx={{ ...oneLineSx, textAlign: 'right' }}>
                        {fmtNum(row.docsMoved)}
                        {kept > 0 && <Box component='span' sx={{ color: 'text.secondary' }}>{` · ${fmtNum(kept)} kept`}</Box>}
                    </Typography>
                )
                return kept > 0
                    ? <Tooltip title={`${fmtNum(kept)} row(s) kept in place because they were updated recently; they move on a later run.`} arrow>{cell}</Tooltip>
                    : cell
            },
        },
        { field: 'sizeBytes', headerName: 'Size', width: 86, ...numCol, valueFormatter: ({ value }) => fmtBytes(value) },
        {
            field: 's3', headerName: 'S3', width: 130, sortable: false,
            renderCell: ({ row }) => (row.s3Url ? (
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%', gap: '2px' }}>
                    <Tooltip title={`${row.s3Url}${row.s3Parts > 0 ? ` · ${fmtNum(row.s3Parts)} part${row.s3Parts === 1 ? '' : 's'}` : ''}`} arrow>
                        <Typography component='span' sx={{ ...oneLineSx, flex: 1 }}>
                            {row.s3Parts > 0 ? fmtBytes(row.s3Bytes) : '—'}
                        </Typography>
                    </Tooltip>
                    <CopyIcon text={row.s3Url} title='Copy S3 URL' />
                    {row.s3Parts > 0 && (
                        <Tooltip title={`Download parts (${fmtNum(row.s3Parts)})`} arrow>
                            <IconButton size='small' onClick={() => setLinksRun(row)} sx={{ p: '2px' }} aria-label='Download parts'>
                                <CloudDownloadOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ) : DASH),
        },
        {
            field: 'startedAt', headerName: 'Started', width: 130, sortable: false,
            renderCell: ({ row }) => {
                if (!row.startedAt) return DASH
                const took = row.status === 'RUNNING' || row.finishedAt ? fmtDuration(row.startedAt, row.finishedAt) : ''
                return (
                    <Tooltip title={fmtDateTime(row.startedAt)} arrow>
                        <Typography component='span' sx={oneLineSx}>
                            {formatDate1(row.startedAt, 'dd MMM, HH:mm')}
                            {took && <Box component='span' sx={{ color: 'text.secondary' }}>{` · ${took}`}</Box>}
                        </Typography>
                    </Tooltip>
                )
            },
        },
        {
            field: 'triggeredBy', headerName: 'By', width: 84, sortable: false,
            renderCell: ({ row }) => {
                if (!row.triggeredBy) return DASH
                const tip = [row.triggeredBy, row.node ? `on ${row.node}` : ''].filter(Boolean).join(' ')
                return (
                    <Tooltip title={tip} arrow>
                        <Typography component='span' sx={oneLineSx}>{shortUser(row.triggeredBy)}</Typography>
                    </Tooltip>
                )
            },
        },
        {
            field: 'actions', headerName: '', width: 75, sortable: false, align: 'right',
            renderCell: ({ row }) => (row.status === 'QUEUED' ? (
                <Button size='small' color='error' variant='outlined' onClick={() => setCancelRun(row)} sx={{ textTransform: 'none', fontWeight: 600, py: 0.25, minWidth: 0 }}>Cancel</Button>
            ) : null),
        },
    ], [])

    return (
        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Stack direction='row' alignItems='baseline' sx={{ gap: 1, mb: 1.5 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>Run history</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {fmtNum(count)} run{count === 1 ? '' : 's'}{hasActive ? ' · auto-refreshing every 30 s while runs are queued or running' : ''}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {loading && <CircularProgress size={16} sx={{ alignSelf: 'center' }} />}
            </Stack>

            <Box sx={{ mb: 1.5 }}>
            <FilterRow>
                <FilterField>
                    <FieldLabel>Collection</FieldLabel>
                    <TextField select size='small' fullWidth value={pending.collection}
                        SelectProps={{ displayEmpty: true }}
                        onChange={(e) => setPending((p) => ({ ...p, collection: e.target.value }))}>
                        <MenuItem value=''>All collections</MenuItem>
                        {collectionNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField>
                    <FieldLabel>Status</FieldLabel>
                    <TextField select size='small' fullWidth value={pending.status}
                        SelectProps={{ displayEmpty: true }}
                        onChange={(e) => setPending((p) => ({ ...p, status: e.target.value }))}>
                        <MenuItem value=''>All statuses</MenuItem>
                        {RUN_STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterDateField>
                    <FieldLabel>Queued between</FieldLabel>
                    <DateRangePicker onChange={pickRange} hideOkButton size='small' defaultRange={pickerRange} />
                </FilterDateField>
                <FilterActions>
                    <ApplyButton variant='contained' size='small' onClick={applyFilters} disabled={loading}>Filter</ApplyButton>
                    <ResetButton variant='contained' size='small' onClick={resetFilters}>Reset</ResetButton>
                </FilterActions>
            </FilterRow>
            </Box>

            <ProzoDataGrid
                rows={rows}
                columns={columns}
                rowCount={count}
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={(s) => { setSizePerPage(s); setPage(0) }}
                getRowId={(r) => r.id || `${r.runId}-${r.collectionName}-${r.period}-${r.fromTier}`}
                columnVisibilityModel={columnVisibilityModel}
                getRowHeight={() => 48}
                sx={{
                    // Tighter than the shared 20 px so every column fits the page without a sideways scroll.
                    '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': { paddingLeft: '10px', paddingRight: '10px' },
                    '& .MuiDataGrid-cell': { fontVariantNumeric: 'tabular-nums' },
                    '& .MuiDataGrid-cellContent': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
                loading={loading}
                exportFileName='archive-runs'
            />

            <S3LinksDialog open={Boolean(linksRun)} run={linksRun} onClose={() => setLinksRun(null)} />
            <ConfirmDialog
                open={Boolean(cancelRun)}
                title='Cancel queued run?'
                confirmLabel='Cancel run'
                tone='error'
                busy={cancelling}
                onCancel={() => setCancelRun(null)}
                onConfirm={confirmCancel}>
                {cancelRun && <>The queued <b>{tierLabel(cancelRun.fromTier)} → {tierLabel(cancelRun.toTier)}</b> move of <b>{cancelRun.collectionName}</b> for <b>{cancelRun.period}</b> will not run.</>}
            </ConfirmDialog>
        </Paper>
    )
}
